import { env } from '../config/env';

/**
 * Maximum chat message length, mirrored from the API's LoanChatController.MaxMessageLength.
 */
export const MAX_MESSAGE_LENGTH = 4000;

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const jsonHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...authHeaders(token),
});

/**
 * Parses a single SSE event block (lines separated by '\n') into { eventType, data }.
 * Exported for unit testing.
 */
export function parseSseEvent(block) {
  const lines = block.split('\n');
  let eventType = 'message';
  let data = '';

  for (const line of lines) {
    // SSE comment lines (e.g. keep-alive heartbeats) — ignore per the SSE spec.
    if (line.startsWith(':')) continue;
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      data = line.slice(6);
    }
  }

  return { eventType, data };
}

/**
 * Reads an SSE response body stream and invokes onEvent(eventType, decodedData)
 * for every event. Data payloads are JSON-decoded when possible (the API JSON-encodes
 * values so newlines survive SSE transport). The terminal "[DONE]" marker is skipped.
 * Exported for unit testing with mock streams.
 */
export async function readSseStream(body, onEvent) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      const { eventType, data } = parseSseEvent(part);
      if (data === '[DONE]') continue;

      let decoded = data;
      try {
        decoded = JSON.parse(data);
      } catch {
        // use raw if not valid JSON
      }

      if (decoded !== '' && decoded != null) {
        onEvent(eventType, decoded);
      }
    }
  }
}

async function throwResponseError(response, fallbackMessage) {
  const errorData = await response.json().catch(() => null);
  throw new Error(errorData?.message || `${fallbackMessage} (${response.status})`);
}

async function consumeChatStream(response, { onContent, onStatus, onMessageId }) {
  if (!response.ok) {
    await throwResponseError(response, 'Request failed');
  }

  await readSseStream(response.body, (eventType, data) => {
    if (eventType === 'status') {
      onStatus?.(data);
    } else if (eventType === 'message-id') {
      const messageId = parseInt(data, 10);
      if (!Number.isNaN(messageId)) onMessageId?.(messageId);
    } else {
      onContent?.(data);
    }
  });
}

/**
 * Streams an AI chat reply over SSE. Invokes the handlers as events arrive.
 */
export async function streamChat({ loanId, message, intent, token, signal, onContent, onStatus, onMessageId }) {
  const response = await fetch(`${env.apiUrl}/loan-chat`, {
    method: 'POST',
    headers: {
      ...jsonHeaders(token),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ loanId, message, intent: intent || undefined }),
    signal,
  });

  await consumeChatStream(response, { onContent, onStatus, onMessageId });
}

/**
 * Re-streams a fresh reply for the conversation's most recent user message.
 */
export async function regenerateChat({ loanId, token, signal, onContent, onStatus, onMessageId }) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}/regenerate`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      Accept: 'text/event-stream',
    },
    signal,
  });

  await consumeChatStream(response, { onContent, onStatus, onMessageId });
}

export async function getChatHistory(loanId, token) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to load chat history');
  return response.json();
}

export async function getChatPresets(loanId, token) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}/presets`, {
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to load presets');
  return response.json();
}

export async function clearChatConversation(loanId, token) {
  const response = await fetch(`${env.apiUrl}/loan-chat/${loanId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to clear conversation');
}

export async function runChatScenario({ loanId, scenarioType, value, token }) {
  const response = await fetch(`${env.apiUrl}/loan-chat/scenario`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ loanId, scenarioType, value }),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to run scenario');
  return response.json();
}

export async function postChatTelemetry({
  loanId,
  scope,
  eventType,
  intent,
  presetKey,
  conversationKey,
  metadata,
  token,
}) {
  await fetch(`${env.apiUrl}/loan-chat/telemetry`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({
      loanId: loanId || undefined,
      scope: scope || undefined,
      eventType,
      intent: intent || undefined,
      presetKey: presetKey || undefined,
      conversationKey: conversationKey || undefined,
      metadata: metadata || {},
    }),
  });
}

export async function postChatFeedback({ loanId, scope, conversationKey, messageId, rating, token }) {
  const response = await fetch(`${env.apiUrl}/loan-chat/feedback`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({
      loanId: loanId || undefined,
      scope: scope || undefined,
      conversationKey: conversationKey || undefined,
      messageId: messageId || undefined,
      rating,
    }),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to submit feedback');
}

/**
 * Streams a Quick Pricer scenario-chat reply. Body must include pricingRequest.
 */
export async function streamScenarioChat({
  message,
  pricingRequest,
  lastRunSummary,
  token,
  signal,
  onContent,
  onStatus,
  onMessageId,
}) {
  const response = await fetch(`${env.apiUrl}/scenario-chat`, {
    method: 'POST',
    headers: {
      ...jsonHeaders(token),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      message,
      pricingRequest,
      lastRunSummary: lastRunSummary || undefined,
    }),
    signal,
  });

  await consumeChatStream(response, { onContent, onStatus, onMessageId });
}

/**
 * Regenerates the last scenario-chat reply. Requires pricingRequest for tool binding.
 */
export async function regenerateScenarioChat({
  pricingRequest,
  lastRunSummary,
  token,
  signal,
  onContent,
  onStatus,
  onMessageId,
}) {
  const response = await fetch(`${env.apiUrl}/scenario-chat/regenerate`, {
    method: 'POST',
    headers: {
      ...jsonHeaders(token),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      pricingRequest,
      lastRunSummary: lastRunSummary || undefined,
      // Message unused by regenerate but keeps the DTO shape valid.
      message: 'regenerate',
    }),
    signal,
  });

  await consumeChatStream(response, { onContent, onStatus, onMessageId });
}

export async function getScenarioChatHistory(token) {
  const response = await fetch(`${env.apiUrl}/scenario-chat`, {
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to load scenario chat history');
  return response.json();
}

export async function clearScenarioChatConversation(token) {
  const response = await fetch(`${env.apiUrl}/scenario-chat`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!response.ok) await throwResponseError(response, 'Failed to clear scenario conversation');
}

/** Client-side quick actions for Quick Pricer scenario chat (no loan presets endpoint). */
export const SCENARIO_CHAT_QUICK_ACTIONS = [
  {
    categoryKey: 'pricing',
    categoryLabel: 'Pricing questions',
    presets: [
      {
        presetKey: 'scenario_buy_down',
        label: 'Worth buying down?',
        description: 'Compare points vs rate for this scenario',
        promptTemplate: 'Is buying down the rate worth it on this scenario? Compare the cost of points to the monthly savings and breakeven.',
        intent: '',
      },
      {
        presetKey: 'scenario_lock_term',
        label: 'Compare lock terms',
        description: 'Tradeoffs across lock periods',
        promptTemplate: 'How do different lock terms affect price on this scenario? Summarize the tradeoffs.',
        intent: '',
      },
      {
        presetKey: 'scenario_why_family',
        label: 'Why is FHA/VA losing?',
        description: 'Explain product-family gaps',
        promptTemplate: 'Why might FHA or VA be losing to Conventional on this scenario? Ground the answer in the last pricing run.',
        intent: '',
      },
      {
        presetKey: 'scenario_fico_whatif',
        label: 'What if FICO were 780?',
        description: 'Reprice with a higher score',
        promptTemplate: 'What if the borrower’s FICO were 780? Reprice the scenario and summarize how the best execution changes.',
        intent: '',
      },
    ],
  },
];

/**
 * Builds the compact pricing-insights request the API expects from a live
 * pricing result set. Caps products client-side; the server caps again.
 */
export function buildPricingInsightsRequest({
  loanId,
  products = [],
  pick,
  loanAmount,
  scenario = {},
  compType,
} = {}) {
  const mapProduct = (product, rateRow, payment) => {
    if (!product) return null;
    const row = rateRow || product.prices?.[0] || product.parRate || {};
    const adjustments = (row.adjustments?.length ? row.adjustments : product.adjustments) || [];
    return {
      productName: product.productName || product.productShortName,
      family: product.family,
      isEligible: product.isEligible === true,
      rate: row.rate ?? null,
      price: row.adjustedPrice ?? null,
      monthlyPayment: payment ?? null,
      termMonths: product.term ?? null,
      ineligibilityReason: product.ineligibilityReason || product.reasons?.[0] || null,
      adjustments: adjustments.slice(0, 8).map((a) => ({
        description: a.description || a.name || a.type || null,
        // Live pricing API uses `points`; lock/LOS adjustments use `amount`.
        amount: a.amount ?? a.points ?? a.adj ?? a.value ?? null,
      })),
    };
  };

  const compactProducts = products.slice(0, 25).map((p) => {
    const row = p.prices?.[0] || p.parRate;
    const payment = loanAmount != null && row?.rate != null
      ? undefined // server doesn't need PI for every row; pick carries it
      : null;
    return mapProduct(p, row, payment);
  }).filter(Boolean);

  const pickPayload = pick
    ? mapProduct(pick.product, pick.rate, pick.pi)
    : null;

  return {
    loanId: loanId || undefined,
    scenario,
    pick: pickPayload,
    products: compactProducts,
    compType: compType || undefined,
  };
}

/**
 * Fetches Piper pricing insights for a pricing run. Returns { insights: string[] }.
 * On failure returns { insights: [] } so callers never render fake copy.
 */
export async function fetchPricingInsights({ payload, token, signal } = {}) {
  try {
    const response = await fetch(`${env.apiUrl}/loan-chat/pricing-insights`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(payload),
      signal,
    });
    if (!response.ok) return { insights: [] };
    const data = await response.json();
    return { insights: Array.isArray(data?.insights) ? data.insights : [] };
  } catch {
    return { insights: [] };
  }
}
